import { useEffect, useState } from 'react';
import { clearAction, updateAction } from '@/quote/util/update-action';
import {
  PromotionEntityType,
  PromotionStatus,
  REQUEST_SUCCESS_CODE,
  SimpleRevisionTypeEnums,
  apiGetDocumentDetailInfoWithCommonInterface,
  openUrlWithNewWindowOrEmitIframeEvent,
} from '@jove-frontend/uikit';
import { useStateMachine } from 'little-state-machine';

export const useBrokerProductInfo = () => {
  const { state } = useStateMachine({
    updateAction,
    clearAction,
  });
  const [brokerProductFileInfoMap, setBrokerProductFileInfoMap] = useState({
    toba: {
      isCustom: false,
      type: '',
      value: '',
    },
    privacy: {
      isCustom: false,
      type: '',
      value: '',
    },
  });
  const [isTripartiteProductFile, setIsTripartiteProductFile] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);

  /**
   * requests
   */
  const requestViewDocument = async (id) => {
    setFileLoading(true);

    const [res] = await apiGetDocumentDetailInfoWithCommonInterface({
      id,
    });
    if (res?.code === REQUEST_SUCCESS_CODE) {
      openUrlWithNewWindowOrEmitIframeEvent(res.data);
    }
    setFileLoading(false);
  };

  /**
   * effects
   */
  useEffect(() => {
    if (state.promotionInfo) {
      const toba = evaluateTobaInfo();
      const privacy = evaluatePrivacyInfo();
      setBrokerProductFileInfoMap({
        toba,
        privacy,
      });
      setIsTripartiteProductFile([PromotionEntityType.BROKER_PROMOTION].includes(state.promotionInfo?.promotionType));
    }
  }, [state.promotionInfo]);

  /**
   * methods
   */
  const evaluateTobaInfo = () => {
    const { status, payload, promotionType } = (state.promotionInfo || {}) as any;
    const isActive = status === PromotionStatus.ACTIVE;
    const { revision_type, file_url, id } = payload?.quote_terms_document || {};
    const isBrokerPromotion = [PromotionEntityType.BROKER_PROMOTION].includes(promotionType);
    if (isActive && isBrokerPromotion && revision_type === SimpleRevisionTypeEnums.FILE_URL && !!file_url) {
      return {
        isCustom: true,
        type: 'url',
        value: file_url,
      };
    } else if (isActive && isBrokerPromotion && revision_type === SimpleRevisionTypeEnums.DOC_FILE && !!id) {
      return {
        isCustom: true,
        type: 'id',
        value: id,
      };
    } else {
      return {
        isCustom: false,
        type: '',
        value: '',
      };
    }
  };

  const evaluatePrivacyInfo = () => {
    const { status, payload, promotionType } = (state.promotionInfo || {}) as any;

    const isActive = status === PromotionStatus.ACTIVE;
    const isBrokerPromotion = [PromotionEntityType.BROKER_PROMOTION].includes(promotionType);
    const { revision_type, file_url, id } = payload?.privacy_policy_document || {};
    if (isActive && isBrokerPromotion && revision_type === SimpleRevisionTypeEnums.FILE_URL && !!file_url) {
      return {
        isCustom: true,
        type: 'url',
        value: file_url,
      };
    } else if (isActive && isBrokerPromotion && revision_type !== SimpleRevisionTypeEnums.FILE_URL && !!id) {
      return {
        isCustom: true,
        type: 'id',
        value: id,
      };
    } else {
      return {
        isCustom: false,
        type: '',
        value: '',
      };
    }
  };

  const openTermsOfBusinessFileAction = (e) => {
    e?.preventDefault();

    const { type, value } = brokerProductFileInfoMap?.toba || {};
    if (type === 'url') {
      openUrlWithNewWindowOrEmitIframeEvent(value);
    } else if (type === 'id') {
      requestViewDocument(value);
    } else {
      openUrlWithNewWindowOrEmitIframeEvent('https://www.jove.co/legal/terms-of-business-agreement');
    }
  };

  const openPrivacyPolicyFileAction = (e) => {
    e?.preventDefault();

    const { type, value } = brokerProductFileInfoMap?.privacy || {};
    if (type === 'url') {
      openUrlWithNewWindowOrEmitIframeEvent(value);
    } else if (type === 'id') {
      requestViewDocument(value);
    } else {
      openUrlWithNewWindowOrEmitIframeEvent('https://www.jove.co/legal/privacy-policy');
    }
  };

  return { fileLoading, isTripartiteProductFile, brokerProductFileInfoMap, openTermsOfBusinessFileAction, openPrivacyPolicyFileAction };
};
